// Tema claro/oscuro del portal, compartido por el sitio público (landing y
// acceso) y el portal autenticado. Modelo de dos estados + «auto» implícito:
//
//   · localStorage['cargados-theme'] = 'light' | 'dark' → preferencia
//     explícita del usuario (la escribe el toggle) y gana siempre: un cambio
//     del sistema se ignora mientras la clave exista.
//   · Clave ausente → «auto»: sigue prefers-color-scheme del sistema, al
//     cargar (el script de index.html fija html[data-theme] antes del primer
//     pintado) y en vivo vía listener 'change' del matchMedia.
//
// No hay un tercer estado «auto» en el toggle — complicaría el botón sin
// ganancia real; para volver a seguir al sistema basta borrar la clave
// (p. ej. desde DevTools) o no haber tocado nunca el toggle.

import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';
export const THEME_KEY = 'cargados-theme';

const mqLight = () => window.matchMedia('(prefers-color-scheme: light)');
const systemTheme = (): Theme =>
  typeof window.matchMedia === 'function' && mqLight().matches ? 'light' : 'dark';

/** Preferencia explícita guardada por el toggle; null = seguir al sistema. */
function storedTheme(): Theme | null {
  try {
    const t = localStorage.getItem(THEME_KEY);
    return t === 'light' || t === 'dark' ? t : null;
  } catch { return null; }
}

export function useTheme(): [Theme, () => void] {
  // Sin flash: el tema inicial se resuelve en el estado (preferencia guardada
  // o, en su ausencia, prefers-color-scheme), no en un efecto posterior —
  // coincide con lo que ya pintó el script de index.html en html[data-theme].
  const [theme, setTheme] = useState<Theme>(() => storedTheme() ?? systemTheme());

  // html[data-theme] siempre refleja el tema activo (respaldo del script de
  // index.html por si el hook corre donde ese script no existe).
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // «Auto» en vivo: solo sin preferencia guardada, el cambio del sistema se
  // aplica al momento; con clave guardada el override gana y se ignora.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = mqLight();
    const onChange = () => {
      if (storedTheme()) return;
      setTheme(mq.matches ? 'light' : 'dark');
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const toggle = useCallback(() => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem(THEME_KEY, next); } catch { /* solo en memoria */ }
    setTheme(next);
  }, [theme]);

  return [theme, toggle];
}
